import { Injectable } from '@angular/core';
import { PushNotificationService } from '@y42/push-notification';
import { UserUIModel } from '@y42/user/models';
import { assertNonNullable } from '@y42/util/assert';
import { BehaviorSubject, finalize, map, Observable, of, tap } from 'rxjs';
import { OrgQuery } from '../state';

enum PresenceEvent {
  SubscriptionSucceeded = 'pusher:subscription_succeeded',
  MemberAdded = 'pusher:member_added',
  MemberRemoved = 'pusher:member_removed',
}

type UserID = UserUIModel['id'];
export type User = Pick<UserUIModel, 'id' | 'first_name' | 'last_name' | 'profile_pic'>;
type UserMap = Record<UserID, User>;

@Injectable({ providedIn: 'root' })
export class PresenceTrackerService {
  constructor(private readonly pushNotificationService: PushNotificationService, private readonly orgQuery: OrgQuery) {}

  private readonly userPresenceByIdentifier$$: Record<string, BehaviorSubject<UserMap>> = {};

  /**
   * Returns a list of users that are "present" under the given identifier.
   *
   * Automatically removes the current user from the presence tracking
   * when the Observable is unsubscribed.
   */
  trackPresence(identifier: string): Observable<User[]> {
    const channelName = this.getPresenceChannelName(identifier);
    const presence$$ = this.getPresenceSubject(identifier);

    if (presence$$) {
      return presence$$.pipe(
        finalize(() => this.leavePresence(identifier)),
        map((userMap) => Object.values(userMap)),
      );
    }

    if (!presence$$) {
      this.userPresenceByIdentifier$$[identifier] = new BehaviorSubject({});
    }

    const presenceChannel = this.pushNotificationService.openChannel(channelName);

    presenceChannel
      .listen<{ members: UserMap }>(PresenceEvent.SubscriptionSucceeded)
      .pipe(tap(({ members }) => this.addMembers(identifier, members)))
      .subscribe();

    presenceChannel
      .listen<{ id: User['id']; info: User }>(PresenceEvent.MemberAdded)
      .pipe(tap(({ id, info: member }) => this.addMember(identifier, id, member)))
      .subscribe();

    presenceChannel
      .listen<{ id: User['id'] }>(PresenceEvent.MemberRemoved)
      .pipe(tap(({ id }) => this.removeMember(identifier, id)))
      .subscribe();

    const userPresence$$ = this.userPresenceByIdentifier$$[identifier];

    if (!userPresence$$) {
      console.warn(`[PresenceTracker]: no user presence found for identifier "${identifier}"`);
      return of([]);
    }

    return userPresence$$.pipe(
      finalize(() => this.leavePresence(identifier)),
      map((userMap) => Object.values(userMap)),
    );
  }

  leavePresence(identifier: string) {
    const channelName = this.getPresenceChannelName(identifier);
    if (!channelName) {
      return;
    }

    this.pushNotificationService.close(channelName);
    this.userPresenceByIdentifier$$[identifier]?.complete();
    delete this.userPresenceByIdentifier$$[identifier];
  }

  private addMembers(identifier: string, members: UserMap) {
    const presence$$ = this.getPresenceSubject(identifier);
    if (!presence$$) {
      return;
    }

    const users: UserMap = {
      ...presence$$.value,
      ...members,
    };
    presence$$.next(users);
  }

  private addMember(identifier: string, id: User['id'], member: User) {
    const presence$$ = this.getPresenceSubject(identifier);
    if (!presence$$) {
      return;
    }

    const users = {
      ...presence$$.value,
      [id]: member,
    };
    presence$$.next(users);
  }

  private removeMember(identifier: string, id: User['id']) {
    const presence$$ = this.getPresenceSubject(identifier);
    if (!presence$$) {
      return;
    }

    const users: UserMap = { ...presence$$.value };
    delete users[id];
    presence$$.next(users);
  }

  private getPresenceSubject(identifier: string): BehaviorSubject<UserMap> | null {
    return this.userPresenceByIdentifier$$[identifier] || null;
  }

  private getPresenceChannelName(identifier: string): string {
    const orgId = this.orgQuery.getActiveId();
    assertNonNullable(`[PresenceTracker]: need an org-id to track presence of a user`);

    return `presence-company.${orgId}.${this.hashString(identifier)}`;
  }

  private hashString(str: string): number {
    // see https://gist.github.com/hyamamoto/fd435505d29ebfa3d9716fd2be8d42f0
    let h = 0;
    for (let i = 0; i < str.length; i++) {
      // eslint-disable-next-line no-bitwise
      h = Math.imul(31, h) | str.charCodeAt(i) | 0;
    }
    return h;
  }
}
